import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useSpring } from "framer-motion";
import { scrollProgressMV } from "../lib/scrollMotion";
import { Menu, X } from "lucide-react";
import { MagneticButton } from "./primitives/MagneticButton";
import { scrollToTarget } from "../lib/scroll";

const LINKS = [
  { label: "Practice Areas", href: "#practice" },
  { label: "Framework", href: "#framework" },
  { label: "Philosophy", href: "#manifesto" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
];

const MARK_RATIO = 1.049;
const FULL_RATIO = 3.992;
const EASE = [0.22, 1, 0.36, 1];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [nearTop, setNearTop] = useState(false);
  const [open, setOpen] = useState(false);
  const [logoHover, setLogoHover] = useState(false);
  const [compact, setCompact] = useState(false);
  const nearRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const apply = () => setCompact(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const progress = useSpring(scrollProgressMV, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const near = e.clientY <= 80;
      if (near !== nearRef.current) {
        nearRef.current = near;
        setNearTop(near);
      }
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const expanded = !scrolled || nearTop;
  // Explicit lookup avoids nested-ternary noise while keeping the exact same values
  const sizeFor = (mobile, compactBar, largeBar) => (compact ? (expanded ? mobile[0] : mobile[1]) : expanded ? largeBar : compactBar);
  const barH = sizeFor([82, 66], 76, 116);
  const logoH = sizeFor([48, 40], 50, 72);

  const go = (href) => {
    setOpen(false);
    scrollToTarget(href);
  };

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-50"
      data-testid="main-nav"
      data-collapsed={scrolled && !nearTop}
    >
      <motion.div
        animate={{
          height: barH,
          backgroundColor: scrolled ? "rgba(9,9,11,0.72)" : "rgba(9,9,11,0)",
          backdropFilter: scrolled ? "blur(20px)" : "blur(0px)",
          borderBottomColor: scrolled ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0)",
        }}
        transition={{ duration: 0.55, ease: EASE }}
        style={{ WebkitBackdropFilter: scrolled ? "blur(20px)" : "blur(0px)" }}
        className="relative flex items-center border-b"
      >
        <div className="mx-auto flex w-[min(1240px,94vw)] items-center justify-between">
          {/* Logo with hover cross-fade */}
          <button
            onClick={() => go(0)}
            onMouseEnter={() => setLogoHover(true)}
            onMouseLeave={() => setLogoHover(false)}
            className="relative block shrink-0"
            data-testid="nav-logo"
            aria-label="THS Consultancy"
          >
            <motion.div
              className="relative"
              animate={{ height: logoH, width: logoH * (logoHover ? FULL_RATIO : MARK_RATIO) }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <motion.img
                src="/logos/ths-mark.png"
                alt="THS"
                draggable={false}
                className="absolute left-0 top-0 h-full w-auto max-w-none select-none"
                animate={{ opacity: logoHover ? 0 : 1, x: logoHover ? -8 : 0 }}
                transition={{ duration: 0.4, ease: EASE }}
              />
              <motion.img
                src="/logos/ths-full.png"
                alt="THS Consultancy"
                draggable={false}
                className="absolute left-0 top-0 h-full w-auto max-w-none select-none"
                animate={{ opacity: logoHover ? 1 : 0, x: logoHover ? 0 : 8 }}
                transition={{ duration: 0.4, ease: EASE }}
              />
              {/* Outline/glow overlay on the extended logo — same treatment as the footer wordmark */}
              <motion.img
                src="/logos/ths-full.png"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="pointer-events-none absolute left-0 top-0 h-full w-auto max-w-none select-none [clip-path:inset(0_0_0_29%)] [filter:drop-shadow(0_0_1.2px_rgba(255,240,210,0.9))_drop-shadow(0_2px_4px_rgba(0,0,0,0.6))]"
                animate={{ opacity: logoHover ? 1 : 0, x: logoHover ? 0 : 8 }}
                transition={{ duration: 0.4, ease: EASE }}
              />
            </motion.div>
          </button>

          {/* Desktop links */}
          <nav className="hidden items-center gap-1 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="group relative rounded-full px-5 py-2 text-base text-muted-foreground transition-colors duration-300 hover:text-foreground lg:text-lg"
                data-testid={`nav-link-${l.href.replace("#", "")}`}
              >
                {l.label}
                <span className="absolute inset-x-5 -bottom-0.5 h-px origin-left scale-x-0 bg-amber transition-transform duration-300 group-hover:scale-x-100" />
              </button>
            ))}
          </nav>

          <div className="hidden md:block">
            <MagneticButton onClick={() => go("#book")} data-testid="nav-book-btn">
              Book Consultation
            </MagneticButton>
          </div>

          {/* Mobile toggle */}
          <button
            className="grid h-10 w-10 place-items-center rounded-full bg-white/5 md:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Scroll progress line */}
        <motion.div
          style={{ scaleX: progress }}
          className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-gradient-to-r from-gold via-amber to-gold"
          data-testid="scroll-progress"
        />
      </motion.div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="glass-strong mx-auto mt-2 w-[min(1240px,94vw)] overflow-hidden rounded-3xl p-3 md:hidden"
          >
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="block w-full rounded-2xl px-4 py-3 text-left text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground"
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("#book")}
              className="mt-1 block w-full rounded-2xl bg-amber px-4 py-3 text-center text-sm font-semibold text-ink"
              data-testid="nav-mobile-book"
            >
              Book Consultation
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
